import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { type components } from '@libraries/typescript/api_client';

type AcceptanceNoteReason =
    components['schemas']['HHAExchangeAcceptanceNoteReason'];

type HHAExchangeAcceptanceNoteReasonSelectorProps = {
    readonly reasons: ReadonlyArray<AcceptanceNoteReason> | undefined;
    readonly selectedReasonId: number | null;
    readonly isLoading?: boolean;
    readonly error?: unknown;
    readonly reasonError?: string | null;
    readonly disabled?: boolean;
    readonly onReasonChange: (reasonId: number | null) => void;
};

export function HHAExchangeAcceptanceNoteReasonSelector({
    reasons,
    selectedReasonId,
    isLoading,
    error,
    reasonError,
    disabled,
    onReasonChange,
}: HHAExchangeAcceptanceNoteReasonSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const reasonList = reasons ?? [];
    const selectedReason = reasonList.find(
        (reason) => reason.id === selectedReasonId,
    );
    const lookupFailed = Boolean(error || reasonError);

    const reasonCombobox = lookupFailed ? (
        <p className="text-muted-foreground text-xs">
            Reasons couldn’t be loaded. Assignment can still continue; the case
            note will be skipped.
        </p>
    ) : (
        <Combobox
            open={isOpen}
            onOpenChange={setIsOpen}
            displayValue={selectedReason?.name}
            placeholder={
                isLoading
                    ? 'Loading reasons…'
                    : reasonList.length === 0
                      ? 'No reasons available'
                      : 'Skip case note'
            }
            disabled={disabled || isLoading || reasonList.length === 0}
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search note reasons…"
            triggerClassName="text-left overflow-hidden"
            triggerProps={{ 'aria-label': 'Case Note Reason' }}
            contentClassName="w-80 p-1"
            contentSide="bottom"
            contentSideOffset={8}
            commandListProps={{
                className: 'max-h-64 overflow-y-auto overscroll-contain',
                onWheel: (event) => {
                    event.stopPropagation();
                    event.currentTarget.scrollTop += event.deltaY;
                },
            }}
        >
            <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                No note reasons found
            </CommandEmpty>
            <CommandItem
                value="Skip case note"
                onSelect={() => {
                    setIsOpen(false);
                    onReasonChange(null);
                }}
                className="py-2 text-left text-sm"
            >
                Skip case note
            </CommandItem>
            {reasonList.map((reason) => (
                <CommandItem
                    key={reason.id}
                    value={`${reason.name} ${reason.description ?? ''} ${reason.id}`}
                    onSelect={() => {
                        setIsOpen(false);
                        onReasonChange(reason.id);
                    }}
                    className="flex flex-col items-start py-2 text-left text-sm"
                >
                    <span>{reason.name}</span>
                    {reason.description ? (
                        <span className="text-muted-foreground text-xs">
                            {reason.description}
                        </span>
                    ) : null}
                </CommandItem>
            ))}
        </Combobox>
    );

    return (
        <div className="border-warning/40 bg-warning/5 mt-2 rounded-md border p-3">
            <div className="flex gap-2">
                <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" />
                <div className="min-w-0 flex-1 space-y-2">
                    <div>
                        <p className="text-xs font-medium">Case Note Reason</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">
                            {isLoading
                                ? 'Checking HHAeXchange case-note reasons…'
                                : lookupFailed
                                  ? 'HHAeXchange case-note reasons are unavailable'
                                  : 'HHAeXchange doesn’t offer “Open Shift” for this patient'}
                        </p>
                        <p className="text-muted-foreground mt-0.5 text-xs">
                            {isLoading
                                ? 'Assignment can continue while reasons load; the note will be skipped if none is selected.'
                                : 'Choose another reason for this case note, or leave it blank to assign without writing the note.'}
                        </p>
                    </div>
                    {reasonCombobox}
                </div>
            </div>
        </div>
    );
}
